import { msg } from '@lingui/core/macro';

import type { LocalizedMessage } from '../../lib/i18n/messages';

export type PublicFaqItem = {
    question: LocalizedMessage;
    answer: LocalizedMessage;
    link?: {
        label: LocalizedMessage;
        href: string;
        spanishHref: string;
    };
};

/** Shared by the visible home FAQ and its FAQPage structured data. */
export const HOME_FAQ_ITEMS = [
    {
        question: msg({
            id: 'home.faq.scholarDifference.question',
            message: 'How is Paperzilla different from Google Scholar alerts?',
            comment: 'Home FAQ question. Also emitted in public FAQPage JSON-LD.',
        }),
        answer: msg({
            id: 'home.faq.scholarDifference.answer',
            message: 'Google Scholar alerts tell you when papers match a query. Paperzilla can accept forwarded alerts, combine them with monitored research sources, and filter the results against your specific research topic so you get a focused feed instead of another noisy inbox.',
            comment: 'Home FAQ answer. Also emitted in public FAQPage JSON-LD.',
        }),
    },
    {
        question: msg({
            id: 'home.faq.morningBrief.question',
            message: 'Can I get a daily research brief in Claude or ChatGPT?',
            comment: 'Home FAQ question. Preserve Claude and ChatGPT. Also emitted in public FAQPage JSON-LD.',
        }),
        answer: msg({
            id: 'home.faq.morningBrief.answer',
            message: 'Yes. Connect Paperzilla to Claude, ChatGPT, or any AI assistant that supports MCP. Each morning it reads the new papers in your feeds, picks the ones worth your time, explains why, and answers follow-up questions from the papers themselves.',
            comment: 'Home FAQ answer. Preserve Claude, ChatGPT, and MCP. Also emitted in public FAQPage JSON-LD.',
        }),
        link: {
            label: msg({
                id: 'home.faq.morningBrief.readMore',
                message: 'See how morning research briefs work',
                comment: 'Link from the home FAQ to the research briefs page.',
            }),
            href: '/research-briefs',
            spanishHref: '/research-briefs',
        },
    },
    {
        question: msg({
            id: 'home.faq.forwardAlerts.question',
            message: 'Can I forward my existing research alerts?',
            comment: 'Home FAQ question. Also emitted in public FAQPage JSON-LD.',
        }),
        answer: msg({
            id: 'home.faq.forwardAlerts.answer',
            message: 'Yes. You can forward Google Scholar-style alerts and other research-alert emails to Paperzilla so they become inputs to your relevance-filtered feed.',
            comment: 'Home FAQ answer. Also emitted in public FAQPage JSON-LD.',
        }),
    },
    {
        question: msg({
            id: 'home.faq.monitoredSources.question',
            message: 'Which sources does Paperzilla monitor directly?',
            comment: 'Home FAQ question. Also emitted in public FAQPage JSON-LD.',
        }),
        answer: msg({
            id: 'home.faq.monitoredSources.answer',
            message: 'Paperzilla monitors preprints from arXiv, bioRxiv, medRxiv, ChemRxiv, ChinaXiv, and IACR ePrint, plus new PubMed-indexed biomedical and clinical records on paid plans. PubMed metadata and abstracts can be useful even when an open full-text PDF is unavailable.',
            comment: 'Home FAQ answer. Preserve source and product names. Also emitted in public FAQPage JSON-LD.',
        }),
    },
    {
        question: msg({
            id: 'home.faq.relevance.question',
            message: 'How does Paperzilla decide what is relevant?',
            comment: 'Home FAQ question. Also emitted in public FAQPage JSON-LD.',
        }),
        answer: msg({
            id: 'home.faq.relevance.answer',
            message: 'You define the research topic. Paperzilla compares incoming papers, datasets, repositories, and patents against that topic, ranks likely matches, and improves over time using feedback and recommender-style signals.',
            comment: 'Home FAQ answer. Also emitted in public FAQPage JSON-LD.',
        }),
    },
    {
        question: msg({
            id: 'home.faq.outputs.question',
            message: 'What outputs does Paperzilla support?',
            comment: 'Home FAQ question. Also emitted in public FAQPage JSON-LD.',
        }),
        answer: msg({
            id: 'home.faq.outputs.answer',
            message: 'Paperzilla can deliver results through an in-app research feed, daily or weekly email digests, RSS/Atom feeds, MCP server access, CLI access, and API access as it becomes available.',
            comment: 'Home FAQ answer. Preserve protocol and product terms. Also emitted in public FAQPage JSON-LD.',
        }),
    },
    {
        question: msg({
            id: 'home.faq.agents.question',
            message: 'Can AI agents use Paperzilla?',
            comment: 'Home FAQ question. Also emitted in public FAQPage JSON-LD.',
        }),
        answer: msg({
            id: 'home.faq.agents.answer',
            message: 'Yes. Paperzilla is designed to provide relevance-filtered research context that AI agents and automation workflows can consume through MCP and CLI access.',
            comment: 'Home FAQ answer. Preserve MCP and CLI. Also emitted in public FAQPage JSON-LD.',
        }),
    },
    {
        question: msg({
            id: 'home.faq.monitorPapersMyself.question',
            message: 'Can’t I use an AI agent to monitor new research papers myself?',
            comment: 'Home FAQ question. Also emitted in public FAQPage JSON-LD.',
        }),
        answer: msg({
            id: 'home.faq.monitorPapersMyself.answer',
            message: 'Yes. For a narrow topic and a few sources, a simple automation may be enough. The harder part is finding useful papers among roughly 10,000 new ones each day, then improving the recommendations with your feedback. Paperzilla handles that filtering; your agent can still brief you on the shortlist.',
            comment: 'Home FAQ answer. Also emitted in public FAQPage JSON-LD.',
        }),
        link: {
            label: msg({
                id: 'home.faq.monitorPapersMyself.readMore',
                message: 'Read the full DIY vs. Paperzilla Q&A',
                comment: 'Link from the home FAQ to the full documentation answer.',
            }),
            href: 'https://docs.paperzilla.ai/answers/can-i-monitor-papers-myself',
            spanishHref: 'https://docs.paperzilla.ai/es/answers/can-i-monitor-papers-myself',
        },
    },
    {
        question: msg({
            id: 'home.faq.productCategory.question',
            message: 'Is Paperzilla a search engine, an alert tool, or a research agent?',
            comment: 'Home FAQ question. Also emitted in public FAQPage JSON-LD.',
        }),
        answer: msg({
            id: 'home.faq.productCategory.answer',
            message: 'Paperzilla is a continuous research data monitoring system. It watches research sources and alerts, filters incoming papers and related research data by relevance, and delivers a structured feed for humans, teams, and AI agents.',
            comment: 'Home FAQ answer. Also emitted in public FAQPage JSON-LD.',
        }),
    },
] as const satisfies readonly PublicFaqItem[];
